import { PROJECTS, PROJECT_RULES, TRADE_GOODS } from '@sd/content';
import { projectTotal, type Command } from '@sd/sim';
import type { Bot } from './match';
import { wellFed } from './fleets';
import { consumptionPerCycle } from './planner';

/** Cycles of its own consumption a bot keeps before giving goods away. */
const RESERVE_CYCLES = 4;
/** Science it keeps for research; above this it gives half of the rest. */
const SCIENCE_KEPT = 40;
/**
 * Its part goes in two or three gifts, not all at once (#148): at most this share
 * of it a time, so the others see the project fill as the deadline nears.
 */
const PART_PER_GIFT = 0.4;

/** How a bot gives to joint projects. */
export interface PatronStyle {
  /** Share of one player's part it puts in: 0 for a free rider, above 1 for the generous. */
  readonly share: number;
  /** Gives science before goods. */
  readonly scienceFirst: boolean;
  /** Gives science only, keeping its goods (the scientist saving for its swarm). */
  readonly scienceOnly?: boolean;
}

/**
 * Phase 2 patron (GDD §9): gives its part to every open project, from goods
 * it can spare and from science, as generous as its style says.
 */
export function makePatron(style: PatronStyle): Bot {
  return (state, playerId, rng) => {
    if (state.pause || style.share <= 0 || state.projects.length === 0 || !rng.chance(0.05)) {
      return [];
    }
    const me = state.players.find((p) => p.id === playerId);
    if (!me || !wellFed(me)) return [];
    const use = consumptionPerCycle(me);
    for (const project of state.projects) {
      if (state.tick >= project.deadline || projectTotal(project) >= project.full) continue;
      const share = PROJECTS[project.kind].share * style.share;
      const owed = Math.min(
        share - (project.byPlayer[playerId]?.points ?? 0),
        Math.ceil(share * PART_PER_GIFT),
      );
      if (owed <= 0) continue;
      const give = (what: Command & { type: 'contributeProject' }) => [what];
      const fromGoods = () => {
        for (const good of PROJECTS[project.kind].goods) {
          const spare = Math.floor(me.goods[good] - use[good] * RESERVE_CYCLES);
          const quantity = Math.min(200, spare, Math.ceil(owed / TRADE_GOODS[good].basePrice));
          if (quantity >= 1) {
            return give({
              type: 'contributeProject',
              playerId,
              projectId: project.id,
              what: good,
              quantity,
            });
          }
        }
        return null;
      };
      const fromScience = () => {
        const quantity = Math.min(
          Math.floor((me.planet.science - SCIENCE_KEPT) / 2),
          Math.ceil(owed / PROJECT_RULES.sciencePrice),
        );
        if (quantity < 1) return null;
        return give({
          type: 'contributeProject',
          playerId,
          projectId: project.id,
          what: 'science',
          quantity,
        });
      };
      const first = style.scienceOnly
        ? fromScience()
        : style.scienceFirst
          ? (fromScience() ?? fromGoods())
          : (fromGoods() ?? fromScience());
      if (first) return first;
    }
    return [];
  };
}

/** Gives one fair part to every project: the phase-2 default. */
export const patronBot: Bot = makePatron({ share: 1, scienceFirst: false });
